/**
 * Phone display formatting.
 *
 * Every mobile in this system is stored as ten bare digits. That is not a
 * display choice — it is the deduplication key. `parse_phone` on the server
 * normalises to ten digits before the duplicate-call check looks a number up
 * and before the importer decides whether two rows are the same candidate, and
 * `candidates` carries a unique constraint on (company_id, phone). So the
 * country code is added here, on the way to the screen, and never on the way
 * to the database.
 *
 * The country code is not shown. Every number on these screens is an Indian
 * mobile rung from India by people who have never needed telling which country
 * they are in, so "+91" is four characters of noise on every row of a table
 * whose whole job is to be scanned. It stays in `telHref`, where a dialler
 * genuinely needs it.
 *
 * Conservative by design: anything that is not a bare ten-digit Indian mobile
 * is returned exactly as stored. Two columns — `users.phone` and
 * `clients.escalation_phone` — have no server-side validation beyond a length
 * cap, so they can hold a landline, an extension, or a number that already
 * carries its own country code. Prefixing those blindly would produce
 * "+91 +91 98765 43210" or a +91 on a number that is not Indian. Leaving them
 * untouched is the only answer that is never wrong.
 */

/** A bare Indian mobile: ten digits, first one 6-9. Mirrors parse_phone. */
const INDIAN_MOBILE = /^[6-9]\d{9}$/;

/**
 * Format a stored number for display: `9876543210` becomes `98765 43210`.
 *
 * Grouped but not prefixed. The five-and-five split is what makes a column of
 * numbers scannable and a transposed digit visible; the country code is what
 * makes it wider without saying anything the reader did not know.
 *
 * Returns null for a null or empty value so call sites keep their own blank
 * rendering — some use an em dash, some a `<Blank />`, some omit the row.
 * Anything unrecognised comes back unchanged rather than being reshaped.
 */
export function formatPhone(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!INDIAN_MOBILE.test(trimmed)) return trimmed;
  return `${trimmed.slice(0, 5)} ${trimmed.slice(5)}`;
}

/**
 * The value for a `tel:` href.
 *
 * E.164 (`+919876543210`, no spaces) rather than the display spelling, because
 * that is what a dialler wants and it stays correct if somebody clicks it while
 * roaming — a bare ten-digit tel: link resolves against whatever country the
 * handset thinks it is in. Unrecognised values pass through with whitespace
 * stripped, which is the most a link can do with a number it cannot classify.
 */
export function telHref(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!INDIAN_MOBILE.test(trimmed)) return trimmed.replace(/\s+/g, '');
  return `+91${trimmed}`;
}


/**
 * What a mobile field should hold as somebody types into it.
 *
 * Ten bare digits, because that is what the database stores and what the
 * duplicate check matches on. A field that accepts `+91 98765 43210` and a
 * field that accepts `9876543210` are describing the same number, and letting
 * both through means the server normalising something the person can already
 * see is wrong.
 *
 * The country code and trunk prefix are dropped, but **only above ten
 * digits**. This matters: `9187654321` is a perfectly ordinary mobile that
 * begins with the digits nine and one, and a rule that stripped a leading "91"
 * on sight would silently mangle it. Length is what distinguishes a country
 * code from the start of a number, which is exactly how `parse_phone` decides
 * it on the server.
 *
 * Typing a full `919876543210` therefore works: the field holds the digits
 * until the eleventh, at which point the leading 91 is recognised for what it
 * is and removed. Pasting works in one step.
 */
export function sanitiseMobileInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length > 10) {
    if (digits.startsWith('091')) return digits.slice(3, 13);
    if (digits.startsWith('91')) return digits.slice(2, 12);
    if (digits.startsWith('0')) return digits.slice(1, 11);
  }
  return digits.slice(0, 10);
}
